// =============================================================================
// components/R2PreviewAnimationEngine.tsx
// Cinematic preview + rich "What You Receive" panel for R2 Marketplace
// =============================================================================
import { useEffect, useRef, useState, useCallback, type JSX } from 'react';
import {
  X, ChevronRight, CheckCircle2, Clock, Zap, Play, Pause,
  FileText, Package, Star, AlertCircle, Sparkles, Plus,
} from 'lucide-react';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:      '#010812',
  cyan:    '#00d4ff',
  orange:  '#ff6b35',
  green:   '#4ade80',
  amber:   '#fbbf24',
  purple:  '#a78bfa',
  text:    '#f0f6ff',
  muted:   'rgba(240,246,255,0.55)',
  dim:     'rgba(0,212,255,0.08)',
  cardBg:  'rgba(6,15,35,0.92)',
  border:  'rgba(0,212,255,0.18)',
};

// ─── Animation types ──────────────────────────────────────────────────────────
export type ServiceAnimationType =
  | 'floor_plan' | 'riser_diagram' | 'data_center' | 'healthcare'
  | 'digital_twin' | 'gis_osp' | 'security' | 'single_line'
  | 'rack_elevation' | 'wifi_design' | 'generic';

interface AnimationConfig {
  title: string; tagline: string; stages: string[]; color: string;
  delivers: string[]; constructionPhases: string[];
}

export const SERVICE_ANIMATION_CONFIG: Record<ServiceAnimationType, AnimationConfig> = {
  floor_plan: {
    title: 'Floor Plan Intelligence',
    tagline: 'R2 is scanning your drawings and generating intelligent layouts…',
    stages: ['Upload PDF/CAD file', 'AI scans drawing elements', 'Rooms recognised & classified', 'Systems overlay generated', 'Documentation package assembled'],
    color: '#00d4ff',
    delivers: ['Intelligent Floor Plan', 'Room Data Sheets', 'Equipment Layout', 'Documentation Package'],
    constructionPhases: ['Structural Shell', 'Interior Partitions', 'MEP Rough-In', 'Finishes & Fixtures', 'Systems Commissioning'],
  },
  riser_diagram: {
    title: 'Riser Diagram Generator',
    tagline: 'Building telecom backbone pathways illuminating floor by floor…',
    stages: ['Building geometry loaded', 'Floor plates stacked (100F)', 'Telecom pathways traced', 'IDF/MDF rooms identified', 'Riser sheets generated'],
    color: '#00d4ff',
    delivers: ['Vertical Backbone Diagram', 'Fiber Pathway Schedule', 'IDF/MDF Room Details', 'Cable Schedule'],
    constructionPhases: ['Core & Shell', 'Telecom Rough-In', 'Riser Cable Pull', 'Terminations', 'Testing & Certification'],
  },
  data_center: {
    title: 'Data Centre Design Package',
    tagline: 'R2 is placing racks, routing power, and designing your digital infrastructure…',
    stages: ['Building shell loaded', 'Rack layout optimised', 'Power pathways calculated', 'Fiber routes generated', 'Cooling zones designed', 'Digital twin activated'],
    color: '#ff6b35',
    delivers: ['Rack Layout Drawing', 'Power Infrastructure Plan', 'Fiber Design Package', 'Equipment Schedule', 'Commissioning Checklist'],
    constructionPhases: ['Raised Floor', 'PDU & UPS Installation', 'Rack Deployment', 'Cable Management', 'Power-On & Testing'],
  },
  healthcare: {
    title: 'Healthcare Facility Design',
    tagline: 'Patient rooms, nurse stations, and clinical systems activating…',
    stages: ['Healthcare facility type detected', 'Room classifications assigned', 'Nurse call infrastructure mapped', 'Clinical systems overlaid', 'Compliance check complete'],
    color: '#4ade80',
    delivers: ['Patient Technology Plan', 'Nurse Call Design', 'Telecom & Data Layout', 'Security & Access Plan', 'Compliance Documentation'],
    constructionPhases: ['Structural & Shell', 'Rough-In MEP', 'Medical Gas Systems', 'Clinical Technology', 'Joint Commission Ready'],
  },
  digital_twin: {
    title: 'Digital Twin Creation',
    tagline: '2D drawings converting to intelligent 3D model with live data layers…',
    stages: ['2D drawings ingested', 'AI geometry extraction', '3D model assembled', 'Asset data attached', 'Live data layers activated'],
    color: '#00d4ff',
    delivers: ['3D BIM Model', 'Asset Information Model', 'Data Layers (MEP, Structure)', 'Lifecycle Tracking', 'Operations Dashboard'],
    constructionPhases: ['As-Designed Capture', '3D Model Build', 'Asset Data Entry', 'Systems Integration', 'Live Dashboard'],
  },
  gis_osp: {
    title: 'OSP & GIS Fiber Design',
    tagline: 'Aerial routing intelligence — plotting fiber paths across your network…',
    stages: ['Site coordinates loaded', 'Aerial map analysed', 'Fiber routes optimised', 'Manholes & handholes placed', 'Complete OSP package generated'],
    color: '#fbbf24',
    delivers: ['OSP Route Drawing', 'Manhole/Handhole Schedule', 'Fiber Splice Plan', 'Permit Package', 'Bill of Materials'],
    constructionPhases: ['Survey & Design', 'Permits', 'Trenching & Conduit', 'Cable Pull & Splice', 'OTDR Testing'],
  },
  security: {
    title: 'Security Systems Design',
    tagline: 'Coverage zones, access points, and surveillance networks activating…',
    stages: ['Floor plan analysed', 'CCTV coverage calculated', 'Access control points identified', 'Alarm zone mapping complete', 'Security documentation assembled'],
    color: '#f472b6',
    delivers: ['CCTV Layout Drawing', 'Access Control Design', 'Intrusion Detection Plan', 'Security Equipment Schedule', 'Compliance Docs'],
    constructionPhases: ['Conduit Rough-In', 'Camera Mounting', 'Controller Installation', 'Door Hardware', 'System Testing'],
  },
  single_line: {
    title: 'Single-Line Diagram',
    tagline: 'Electrical distribution intelligence — power pathways being mapped…',
    stages: ['Load schedule loaded', 'Distribution hierarchy traced', 'Panel schedules generated', 'Protection coordination checked', 'SLD documentation complete'],
    color: '#fbbf24',
    delivers: ['Single-Line Diagram', 'Panel Schedules', 'Load Calculations', 'Coordination Study', 'Arc Flash Documentation'],
    constructionPhases: ['Service Entry', 'Main Distribution', 'Sub-Panels', 'Branch Circuits', 'Commissioning'],
  },
  rack_elevation: {
    title: 'Rack Elevation Generator',
    tagline: 'Equipment placement being optimised — U-space allocation in progress…',
    stages: ['Equipment list loaded', 'U-space calculated', 'Equipment placed optimally', 'Cable management designed', 'Rack elevations generated'],
    color: '#00d4ff',
    delivers: ['Rack Elevation Drawing', 'U-Space Schedule', 'Cable Management Plan', 'Power Load Sheet', 'Labelling Guide'],
    constructionPhases: ['Rack Delivery', 'Equipment Staging', 'Installation', 'Cable Dressing', 'Commissioning'],
  },
  wifi_design: {
    title: 'Wi-Fi Network Design',
    tagline: 'RF propagation model running — access points being positioned…',
    stages: ['Floor plan loaded', 'RF model calculated', 'AP positions optimised', 'Coverage verified', 'Wi-Fi documentation assembled'],
    color: '#a78bfa',
    delivers: ['AP Location Drawing', 'Channel Plan', 'Controller Config', 'Coverage Heat Map', 'Installation Guide'],
    constructionPhases: ['Infrastructure Survey', 'AP Mounting Rough-In', 'AP Installation', 'RF Optimisation', 'Acceptance Testing'],
  },
  generic: {
    title: 'R2 AI Engineering Service',
    tagline: 'R2 intelligence engine initialising…',
    stages: ['Project data loaded', 'AI analysis running', 'Design generated', 'Quality check complete', 'Package assembled'],
    color: '#00d4ff',
    delivers: ['Engineering Drawings', 'Technical Documentation', 'Equipment Schedule', 'Compliance Package'],
    constructionPhases: ['Survey', 'Design', 'Construction', 'Testing', 'Handover'],
  },
};

// ─── Rich Service Delivery Details ───────────────────────────────────────────
interface ServiceDeliveryDetails {
  detailed_delivers: { name: string; description: string }[];
  sample_outputs: string[];
  timeline: { phase: string; duration: string; description: string }[];
  upgrades: { name: string; description: string; tag: string }[];
  why_r2: string;
  standards: string[];
}

const SERVICE_DELIVERY_DETAILS: Record<ServiceAnimationType, ServiceDeliveryDetails> = {
  floor_plan: {
    detailed_delivers: [
      { name: 'Intelligent Floor Plan Drawing', description: 'Full A1/A3 floor plan with rooms classified, dimensioned, and overlaid with telecom/data/power outlet locations' },
      { name: 'Room Data Sheets (RDS)', description: 'Individual RDS per room type with power, data, AV, and special requirements per ANSI/TIA-568' },
      { name: 'Equipment Layout Plan', description: 'Furniture and equipment placement coordinating with infrastructure rough-in points' },
      { name: 'Legends & Schedules', description: 'Full symbol legend, outlet schedule, and drawing register per AS1100 or your project standard' },
      { name: 'PDF + DWG Package', description: 'All drawings issued as editable DWG (AutoCAD 2018+) and PDF for construction use' },
    ],
    sample_outputs: ['Floor plan with 47 room classifications', 'RDS for OR, ICU, and general wards', 'Equipment coordinates exported to BIM'],
    timeline: [
      { phase: 'Intake & Brief', duration: '2h', description: 'PDF/CAD upload processed, AI extracts geometry and classifies spaces' },
      { phase: 'AI Generation', duration: '4h', description: 'Outlet placement, RDS data population, and drawing assembly automated' },
      { phase: 'QA Review', duration: '3h', description: 'Registered engineer reviews AI output against project standards' },
      { phase: 'Client Issue', duration: '1h', description: 'Final drawings issued via secure delivery portal in PDF + DWG' },
    ],
    upgrades: [
      { name: 'Express Delivery', description: '50% faster turnaround — prioritised queue processing', tag: 'POPULAR' },
      { name: 'As-Built Overlay', description: 'Compare against existing as-built drawings with AI change detection', tag: 'NEW' },
      { name: 'BIM Model Integration', description: 'Floor plan data output as IFC/Revit family placements', tag: '' },
    ],
    why_r2: 'R2 has processed over 2,400 healthcare and commercial floor plans. Our AI achieves 94% room classification accuracy and produces code-compliant outlet schedules that typically require 3–5 days manually — in under 10 hours.',
    standards: ['ANSI/TIA-568', 'AS1100', 'IBC', 'NFPA 70'],
  },
  riser_diagram: {
    detailed_delivers: [
      { name: 'Vertical Backbone Riser Diagram', description: 'Complete floor-by-floor telecom riser showing backbone pathways, IDF/MDF locations, and fiber/copper routes' },
      { name: 'Fiber Pathway Schedule', description: 'Tabulated schedule of all conduit/cable tray runs with size, type, and fire rating' },
      { name: 'IDF/MDF Room Layouts', description: 'Detailed equipment room layouts showing rack positions, UPS, and cooling' },
      { name: 'Cable Schedule', description: 'Full cable schedule with types, lengths, and labelling per ANSI/TIA-606' },
    ],
    sample_outputs: ['18-floor riser with 4 IDFs', 'Fiber schedule with 144F backbone', 'MDF room layout per BICSI TDMM'],
    timeline: [
      { phase: 'Building Import', duration: '1h', description: 'Floor plate geometry and stacking plan extracted from submitted files' },
      { phase: 'AI Riser Design', duration: '6h', description: 'Backbone topology generated, IDF locations optimised per BICSI guidelines' },
      { phase: 'Engineer Review', duration: '3h', description: 'PE checks topology, count, and redundancy strategy' },
      { phase: 'Final Issue', duration: '2h', description: 'PDF + DWG package with full revision block and stamp' },
    ],
    upgrades: [
      { name: 'Structured Cabling Design', description: 'Horizontal cabling layout on each floor included', tag: 'POPULAR' },
      { name: 'Commissioning Spec', description: 'OTDR test specification and acceptance criteria document', tag: '' },
    ],
    why_r2: 'R2 was purpose-built for multi-storey telecom backbone design. Our algorithm optimises IDF placement to minimise cable runs while meeting BICSI TDMM distance requirements — saving 40% in cable cost vs traditional layout approaches.',
    standards: ['BICSI TDMM', 'ANSI/TIA-568', 'ANSI/TIA-606', 'NFPA 70'],
  },
  data_center: {
    detailed_delivers: [
      { name: 'Rack Layout Floor Plan', description: 'Dimensioned rack layout with hot/cold aisle containment, spacing per ANSI/TIA-942, and access clearances' },
      { name: 'Power Distribution Design', description: 'PDU sizing, UPS capacity, kW/rack calculations, and panel board schedules' },
      { name: 'Structured Cabling Design', description: 'Top-of-rack vs end-of-row switching topology, fiber inter-rack routing' },
      { name: 'Cooling Zone Analysis', description: 'CRAC/CRAH placement, CFD airflow zones, and raised floor plenum design' },
      { name: 'Equipment Schedule', description: 'Complete Bill of Materials with manufacturer, model, quantity, and kW load' },
      { name: 'Commissioning Checklist', description: '120-point commissioning checklist per ANSI/TIA-942-B and Uptime Institute' },
    ],
    sample_outputs: ['40-rack layout at 12kW/rack', 'Power load schedule 480kW total', 'Tier III topology with 2N UPS'],
    timeline: [
      { phase: 'Scope Brief', duration: '2h', description: 'IT load requirements, rack count, and Tier target confirmed' },
      { phase: 'AI Layout Generation', duration: '8h', description: 'Rack optimisation, power path design, and cooling zone modelling' },
      { phase: 'Mechanical Coordination', duration: '4h', description: 'HVAC and structural review of floor loading and plenum design' },
      { phase: 'Final Package', duration: '2h', description: 'Full drawing set plus commissioning checklist issued in PDF + DWG' },
    ],
    upgrades: [
      { name: 'DCIM Integration Spec', description: 'Data centre infrastructure management system point list and API spec', tag: '' },
      { name: 'Tier IV Upgrade', description: 'Dual-path power and concurrent maintainability design', tag: 'PREMIUM' },
      { name: 'Digital Twin Output', description: 'Rack layout exported as R2 BINS digital twin with live data stubs', tag: 'NEW' },
    ],
    why_r2: 'R2 has designed data centres from 5 racks to 2,000 racks. Our algorithms enforce ANSI/TIA-942 clearances and Uptime Institute cooling requirements automatically — eliminating the most common design errors that cause costly reworks during commissioning.',
    standards: ['ANSI/TIA-942-B', 'Uptime Institute Tier I–IV', 'IEEE 1100', 'NFPA 75'],
  },
  healthcare: {
    detailed_delivers: [
      { name: 'Patient Technology Plan', description: 'Bedhead unit locations, nurse call pendant placement, TV/comm outlet positions per ANSI/TIA-1179' },
      { name: 'Nurse Call System Design', description: 'Complete IP nurse call topology, zone configuration, and dome light placement per AS3811' },
      { name: 'Clinical ICT Layout', description: 'Computer workstation, medical monitor, and POE device locations per FGI guidelines' },
      { name: 'Security & Access Plan', description: 'CCTV coverage, access control points, and duress alarm placement per clinical requirements' },
      { name: 'Compliance Documentation', description: 'FGI, NFPA 99, and ANSI/TIA-1179 compliance matrix for your AHJ submission' },
    ],
    sample_outputs: ['ICU floor plan with 16-bed nurse call', '48-camera CCTV coverage map', 'FGI compliance matrix 94% pass'],
    timeline: [
      { phase: 'Clinical Brief', duration: '2h', description: 'Facility type, acuity level, and regulatory jurisdiction confirmed' },
      { phase: 'AI System Design', duration: '8h', description: 'Nurse call topology, ICT outlets, and security coverage generated per clinical standards' },
      { phase: 'Infection Control Review', duration: '3h', description: 'Zone isolation, hand hygiene location check, and ICRA overlay applied' },
      { phase: 'Package Issue', duration: '1h', description: 'Construction drawings and compliance documentation issued' },
    ],
    upgrades: [
      { name: 'RTLS Design', description: 'Real-Time Location System reader placement and zone plan for asset and staff tracking', tag: 'POPULAR' },
      { name: 'Medical Equipment Coordination', description: 'R2 EPE room data sheets coordinated with clinical technology layout', tag: '' },
      { name: 'Joint Commission Package', description: 'Pre-survey documentation package for TJC/DNV accreditation review', tag: 'PREMIUM' },
    ],
    why_r2: 'R2 has designed clinical technology systems for 60+ healthcare facilities including Level 1 trauma centres and 500-bed greenfield hospitals. Our AI enforces FGI, NFPA 99, and ANSI/TIA-1179 simultaneously — eliminating the most common compliance failures before construction.',
    standards: ['FGI Guidelines', 'NFPA 99', 'ANSI/TIA-1179', 'HL7 FHIR', 'AS3811'],
  },
  digital_twin: {
    detailed_delivers: [
      { name: '3D BIM Model (IFC/RVT)', description: 'LOD 300 3D model of your building with room classification, MEP spaces, and structural elements' },
      { name: 'Asset Information Model (AIM)', description: 'All installed assets tagged with manufacturer, model, install date, warranty, and maintenance schedule' },
      { name: 'Live Data Layer Integration', description: 'Power, data, HVAC, and access control data stubs connected to real-time DCIM/BMS systems' },
      { name: 'Operations Dashboard', description: 'Web-based dashboard showing real-time asset status, alerts, and maintenance tasks' },
      { name: 'BINS Digital Twin', description: 'Full R2 BINS object hierarchy created — navigate from Campus → Building → Floor → Room → Asset' },
    ],
    sample_outputs: ['12-storey office BIM at LOD 300', '2,400 assets tagged in AIM', 'Live BMS integration for 8 data points'],
    timeline: [
      { phase: 'Drawing Ingestion', duration: '4h', description: 'All 2D drawing PDFs processed by AI geometry engine, spaces extracted' },
      { phase: 'Model Build', duration: '16h', description: '3D model assembled, rooms classified, MEP spaces volumetrically defined' },
      { phase: 'Asset Data Entry', duration: '8h', description: 'Equipment schedule data mapped to asset objects in AIM' },
      { phase: 'Dashboard Configuration', duration: '4h', description: 'Operations dashboard configured with live data connection points' },
    ],
    upgrades: [
      { name: 'LOD 400 Upgrade', description: 'Fabrication-ready model with detailed MEP routing and penetrations', tag: 'PREMIUM' },
      { name: 'BMS/DCIM Integration', description: 'Live data connection to your building management system', tag: '' },
      { name: '4D Construction Simulation', description: 'Construction sequence animation generated from your BIM data', tag: 'NEW' },
    ],
    why_r2: 'R2 built the BINS platform specifically to make digital twins operational — not just visual. Our AIM framework ensures your digital twin stays accurate after handover, with AI-powered maintenance alerts and lifecycle tracking that traditional BIM cannot provide.',
    standards: ['ISO 19650', 'IFC 4.3', 'COBie', 'ANSI/TIA-4966'],
  },
  gis_osp: {
    detailed_delivers: [
      { name: 'OSP Fiber Route Drawing', description: 'Georeferenced route plan showing aerial/underground fiber paths, crossing types, and protection requirements' },
      { name: 'Manhole & Handhole Schedule', description: 'Tabulated schedule with coordinates, depths, types, and duct bank configurations' },
      { name: 'Fiber Splice & Distribution Plan', description: 'Splice location map, cable count schedule, and optical loss budget calculation' },
      { name: 'Permit Application Package', description: 'Council/authority permit drawings in the required format with existing utilities overlay' },
      { name: 'Bill of Materials', description: 'Complete BOQ with cable footage, conduit quantities, hardware, and labour estimates' },
    ],
    sample_outputs: ['14.2km metro fiber route', '32 manhole locations georeferenced', 'Loss budget <3.2dB verified'],
    timeline: [
      { phase: 'Site Survey Data', duration: '4h', description: 'GIS coordinates and existing infrastructure data loaded, aerial imagery processed' },
      { phase: 'Route Optimisation', duration: '8h', description: 'AI analyses ground cover, easements, and crossings to propose optimal route' },
      { phase: 'Engineer Finalisation', duration: '4h', description: 'Route confirmed, splice locations verified, loss budget calculated' },
      { phase: 'Permit Package', duration: '4h', description: 'Authority drawings formatted and BOQ finalised for procurement' },
    ],
    upgrades: [
      { name: 'OTDR Test Specification', description: 'Acceptance test spec with event list and pass/fail criteria per BICSI OSP', tag: '' },
      { name: 'Civil Construction Package', description: 'Trench cross-sections, bedding details, and traffic control plan', tag: 'POPULAR' },
      { name: 'As-Built GPS Survey', description: 'Post-construction GPS logging with GIS shapefile output', tag: '' },
    ],
    why_r2: 'R2 GIS Intelligence processes satellite imagery, council cadastral data, and utility crossing databases simultaneously to produce OSP designs that typically take 3 weeks in under 48 hours. Our route optimisation reduces cable quantity by 12–18% on average vs manual routing.',
    standards: ['BICSI OSP', 'ANSI/TIA-758', 'NECA 301', 'AS/NZS 3080'],
  },
  security: {
    detailed_delivers: [
      { name: 'CCTV System Design', description: 'Camera positions, lens selection, mounting heights, and field-of-view coverage maps per IEC 62676' },
      { name: 'Access Control Design', description: 'Door hardware schedule, reader positions, controller topology, and door force/release timing per AS2201' },
      { name: 'Intrusion Detection Plan', description: 'PIR, door/window contact, and duress device locations with zone mapping' },
      { name: 'Infrastructure Drawings', description: 'Conduit routes, cable types, and power circuit schedule for security systems' },
      { name: 'Compliance Documentation', description: 'AS2201 certification checklist, IEC 62676 grading compliance matrix' },
    ],
    sample_outputs: ['64-camera CCTV with 98% area coverage', '22 access control doors mapped', 'Zone topology for 6 security areas'],
    timeline: [
      { phase: 'Security Brief', duration: '2h', description: 'Risk profile, security grade target, and AHJ requirements confirmed' },
      { phase: 'AI Coverage Analysis', duration: '6h', description: 'AI calculates optimal camera positions for full coverage with minimum blind spots' },
      { phase: 'System Design', duration: '6h', description: 'Access control topology, intrusion zones, and infrastructure routing designed' },
      { phase: 'Package Issue', duration: '2h', description: 'Full drawing set with compliance matrix and equipment schedule' },
    ],
    upgrades: [
      { name: 'Video Analytics Config', description: 'AI video analytics rule configuration for perimeter intrusion and object detection', tag: 'NEW' },
      { name: 'Integration Specification', description: 'VMS/PSIM integration point list for CCTV, access, and intercom systems', tag: '' },
    ],
    why_r2: 'R2 Security Intelligence analyses your floor plan geometry to calculate camera field-of-view coverage mathematically — guaranteeing your specified security grade without expensive over-specification. We have designed security systems for 120+ facilities across healthcare, commercial, and critical infrastructure.',
    standards: ['AS2201', 'IEC 62676', 'NFPA 731', 'UL 681'],
  },
  single_line: {
    detailed_delivers: [
      { name: 'Single-Line Diagram (SLD)', description: 'Complete power distribution SLD showing all switchboards, MCCs, and panel boards with fault current ratings' },
      { name: 'Panel Board Schedules', description: 'Full schedule for every panel showing circuit numbers, breaker sizes, load types, and load values' },
      { name: 'Load Calculations', description: 'NEC/CEC-compliant demand factor load calculations for sizing service entrance and sub-mains' },
      { name: 'Protection Coordination Study', description: 'Upstream/downstream breaker selectivity analysis ensuring discrimination to minimise outage scope' },
      { name: 'Arc Flash Analysis', description: 'Incident energy calculation per IEEE 1584-2018 at key distribution points' },
    ],
    sample_outputs: ['480V/3Ph SLD for 800A service', '32-panel schedule completed', 'Arc flash labels for 12 switchboards'],
    timeline: [
      { phase: 'Load Schedule Input', duration: '2h', description: 'Equipment list and load data processed from submitted schedules' },
      { phase: 'AI SLD Generation', duration: '8h', description: 'Distribution hierarchy drawn, protection ratings calculated automatically' },
      { phase: 'Coordination Study', duration: '6h', description: 'Selectivity analysis run, arc flash calculated per IEEE 1584-2018' },
      { phase: 'Package Issue', duration: '2h', description: 'SLD, panel schedules, and arc flash report issued in PDF + DWG' },
    ],
    upgrades: [
      { name: 'Short Circuit Study', description: 'Maximum available fault current calculations at all major distribution points', tag: 'POPULAR' },
      { name: 'Generator/UPS Sizing', description: 'Backup power system sizing with load shedding strategy', tag: '' },
    ],
    why_r2: 'R2 generates NEC/CEC and AS3000-compliant single-line diagrams in hours, not weeks. Our protection coordination algorithm automatically sizes and graded circuit protection — a process that typically requires a specialist power systems engineer spending 2–3 days.',
    standards: ['NEC/CEC (NFPA 70 / C22.1)', 'AS/NZS 3000', 'IEEE 1584-2018', 'IEC 60909'],
  },
  rack_elevation: {
    detailed_delivers: [
      { name: 'Rack Elevation Drawings', description: 'Front and rear elevation views for every rack showing equipment at accurate U-height positions per EIA-310' },
      { name: 'U-Space Schedule', description: 'Tabulated summary of used/available U-space, total weight, and power per rack and per row' },
      { name: 'Cable Management Plan', description: 'Top and rear view showing horizontal/vertical cable managers, bend radii, and colour-coding' },
      { name: 'Power Load Sheet', description: 'Per-outlet power load, total rack PDU loading, and kW/rack summary' },
      { name: 'Labelling Guide', description: 'Label schedule for assets, patch panels, and ports per ANSI/TIA-606' },
    ],
    sample_outputs: ['24-rack elevation package', 'U-space utilisation 78%', 'Cable management drawing at 1:10'],
    timeline: [
      { phase: 'Equipment List Import', duration: '2h', description: 'Equipment schedule with U-heights and power loads processed by AI' },
      { phase: 'Placement Optimisation', duration: '4h', description: 'Weight distribution and airflow optimised, heavy equipment bottom-mounted' },
      { phase: 'Drawing Generation', duration: '3h', description: 'Elevations drawn, labels applied, cable management overlay added' },
      { phase: 'Package Issue', duration: '1h', description: 'Full rack package with power summary and labelling guide' },
    ],
    upgrades: [
      { name: 'Structured Cabling', description: 'Patch panel population and port assignment schedule', tag: '' },
      { name: 'Cable Route Drawing', description: 'Above-ceiling cable tray and floor pathway routing between racks', tag: 'POPULAR' },
    ],
    why_r2: 'R2 Rack Elevation Generator uses EIA-310 equipment footprint data and your load requirements to produce rack layouts that are immediately usable for installation — no manual drawing required. Equipment catalogue covers 800+ manufacturers.',
    standards: ['EIA-310', 'ANSI/TIA-942', 'ANSI/TIA-606', 'ISO/IEC 11801'],
  },
  wifi_design: {
    detailed_delivers: [
      { name: 'AP Location Drawing', description: 'Floor plan with access point positions, mounting heights, and coverage radii showing predicted signal strength' },
      { name: 'Channel & Frequency Plan', description: '2.4GHz and 5GHz channel assignment map preventing co-channel interference' },
      { name: 'Controller Configuration Spec', description: 'SSID architecture, VLAN assignments, roaming configuration, and QoS settings' },
      { name: 'Coverage Heat Map', description: 'Predicted RSSI coverage map at -65dBm and -75dBm thresholds per Cisco/Aruba validated models' },
      { name: 'Installation & Cabling Guide', description: 'Mounting instructions, PoE switch port schedule, and cable route map' },
    ],
    sample_outputs: ['Floor plan 3,200m² with 28 APs', '-65dBm coverage 99.2%', 'Wi-Fi 6E 6GHz band assigned'],
    timeline: [
      { phase: 'Floor Plan Import', duration: '2h', description: 'Building materials, wall types, and obstruction data extracted for RF model' },
      { phase: 'RF Modelling', duration: '6h', description: 'AI propagation model run at 2.4, 5, and 6GHz with multi-AP interference analysis' },
      { phase: 'Design Finalisation', duration: '4h', description: 'AP positions adjusted for aesthetics and cable routing, channel plan generated' },
      { phase: 'Package Issue', duration: '2h', description: 'Heat maps, installation guide, and controller config spec issued' },
    ],
    upgrades: [
      { name: 'Site Survey Validation', description: 'Post-installation RF site survey compared against predicted design', tag: '' },
      { name: 'IoT/RTLS Integration', description: 'AP configuration for BLE/RFID IoT overlay and location services', tag: 'POPULAR' },
    ],
    why_r2: 'R2 Wi-Fi Design uses validated RF propagation models calibrated against 400+ real-world deployments. Our designs achieve first-survey sign-off 87% of the time vs 54% industry average, saving costly post-construction reworks.',
    standards: ['IEEE 802.11ax', 'Wi-Fi 6/6E', 'ANSI/TIA-1152', 'WPA3-Enterprise'],
  },
  generic: {
    detailed_delivers: [
      { name: 'Engineering Drawings', description: 'Professional-grade drawings produced to Australian/International standards' },
      { name: 'Technical Documentation', description: 'Supporting technical documentation, schedules, and specifications' },
      { name: 'Equipment Schedule', description: 'Complete Bill of Materials with specifications and quantities' },
      { name: 'Compliance Package', description: 'Standards compliance checklist and AHJ submission-ready documentation' },
    ],
    sample_outputs: ['Professional drawing package', 'Full technical documentation', 'Compliance-ready submission'],
    timeline: [
      { phase: 'Project Brief', duration: '2h', description: 'Scope, standards, and deliverable format confirmed' },
      { phase: 'AI Design', duration: '8h', description: 'AI generates design to project brief and applicable standards' },
      { phase: 'Engineer Review', duration: '4h', description: 'Registered engineer reviews and approves all deliverables' },
      { phase: 'Package Issue', duration: '2h', description: 'Final package issued in PDF + DWG via secure portal' },
    ],
    upgrades: [
      { name: 'Express Delivery', description: 'Priority queue — 50% faster turnaround', tag: 'POPULAR' },
      { name: 'Revisions Pack', description: '3 rounds of client-directed revisions included', tag: '' },
    ],
    why_r2: 'R2 has processed 4,000+ engineering packages across 18 disciplines. Our AI-human hybrid workflow produces compliant, construction-ready deliverables at a fraction of traditional consulting timelines and cost.',
    standards: ['AS1100', 'ANSI/ASHRAE', 'ISO 7200', 'IEC 61082'],
  },
};

// ─── Canvas Animation Engine ──────────────────────────────────────────────────
export function useCanvasAnimation(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  serviceType: ServiceAnimationType,
  playing: boolean,
) {
  const animRef = useRef<number>(0);
  const cfg = SERVICE_ANIMATION_CONFIG[serviceType];

  const drawFrame = useCallback((canvas: HTMLCanvasElement, t: number) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.strokeStyle = 'rgba(0,212,255,0.04)'; ctx.lineWidth = 1;
    const gs = 36;
    for (let x = 0; x < W; x += gs) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y < H; y += gs) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
    const phase = (t * 0.0005) % 1;
    if (serviceType === 'riser_diagram' || serviceType === 'digital_twin') drawBuildingRiser(ctx, W, H, phase, cfg.color);
    else if (serviceType === 'data_center') drawDataCenterFloor(ctx, W, H, phase, cfg.color);
    else if (serviceType === 'healthcare') drawHospitalFloor(ctx, W, H, phase, cfg.color);
    else if (serviceType === 'gis_osp') drawFiberMap(ctx, W, H, phase, cfg.color);
    else if (serviceType === 'security') drawSecurityOverlay(ctx, W, H, phase, cfg.color);
    else if (serviceType === 'rack_elevation') drawRackElevation(ctx, W, H, phase, cfg.color);
    else drawGenericAI(ctx, W, H, phase, cfg.color);
    const scanY = ((t * 0.0003) % 1) * H;
    const sg = ctx.createLinearGradient(0, scanY - 30, 0, scanY + 30);
    sg.addColorStop(0, 'rgba(0,212,255,0)'); sg.addColorStop(0.5, 'rgba(0,212,255,0.18)'); sg.addColorStop(1, 'rgba(0,212,255,0)');
    ctx.fillStyle = sg; ctx.fillRect(0, scanY - 30, W, 60);
    for (let i = 0; i < 6; i++) {
      const px = ((i / 6 + t * 0.0004) % 1) * W;
      const py = 30 + i * 20 + Math.sin(((i / 6 + t * 0.0004) % 1) * Math.PI * 4) * 10;
      ctx.beginPath(); ctx.arc(px, py, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(0,212,255,${0.4 + Math.sin(t * 0.003 + i) * 0.3})`; ctx.fill();
    }
  }, [serviceType, cfg.color]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !playing) return;
    let start: number | null = null;
    const animate = (ts: number) => {
      if (!start) start = ts;
      drawFrame(canvas, ts - start);
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [canvasRef, playing, drawFrame]);
}

// ─── Scene drawing helpers ────────────────────────────────────────────────────
function drawBuildingRiser(ctx: CanvasRenderingContext2D, W: number, H: number, phase: number, color: string) {
  const n = 12, fH = H * 0.045, bW = W * 0.35, bX = W * 0.32;
  const vis = Math.floor(phase * n * 2);
  for (let i = 0; i < Math.min(vis, n); i++) {
    const y = H * 0.85 - i * (fH + 2);
    const a = Math.min(1, (vis - i) * 0.3);
    ctx.fillStyle = `rgba(0,212,255,${a * 0.07})`; ctx.strokeStyle = color; ctx.lineWidth = 0.8;
    ctx.beginPath(); ctx.rect(bX, y, bW, fH); ctx.fill(); ctx.stroke();
    if (i < vis - 2) { ctx.beginPath(); ctx.moveTo(bX + bW * 0.2, y + fH * 0.5); ctx.lineTo(bX + bW * 0.8, y + fH * 0.5); ctx.strokeStyle = `rgba(0,212,255,${a * 0.5})`; ctx.lineWidth = 1.5; ctx.stroke(); }
  }
}
function drawDataCenterFloor(ctx: CanvasRenderingContext2D, W: number, H: number, phase: number, _c: string) {
  const rows = 5, cols = 8, rW = W * 0.07, rH = H * 0.2, sX = W * 0.12, sY = H * 0.3, g = 6;
  const vis = Math.floor(phase * rows * cols * 2); let idx = 0;
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
    if (idx++ > vis) break;
    const x = sX + c * (rW + g), y = sY + r * (rH * 0.55 + g), a = Math.min(1, (vis - idx + 1) * 0.4);
    ctx.fillStyle = `rgba(255,107,53,${a * 0.1})`; ctx.strokeStyle = `rgba(255,107,53,${a * 0.7})`; ctx.lineWidth = 0.8;
    ctx.beginPath(); ctx.rect(x, y, rW, rH * 0.5); ctx.fill(); ctx.stroke();
    ctx.fillStyle = `rgba(0,212,255,${a * 0.9})`; ctx.fillRect(x + 2, y + 3, 3, 3);
  }
}
function drawRackElevation(ctx: CanvasRenderingContext2D, W: number, H: number, phase: number, color: string) {
  const rackX = W * 0.30, rackY = H * 0.08, rackW = W * 0.28, rackH = H * 0.84;
  const uH = rackH / 42;
  ctx.fillStyle = 'rgba(3,8,18,0.88)'; ctx.strokeStyle = 'rgba(0,212,255,0.58)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.roundRect(rackX, rackY, rackW, rackH, 10); ctx.fill(); ctx.stroke();
  ctx.fillStyle = 'rgba(148,163,184,0.75)';
  ctx.fillRect(rackX - 10, rackY + 5, 5, rackH - 10); ctx.fillRect(rackX + rackW + 5, rackY + 5, 5, rackH - 10);
  const visibleRows = Math.min(42, Math.floor(phase * 64));
  for (let i = 0; i < visibleRows; i++) {
    const y = rackY + i * uH;
    ctx.strokeStyle = 'rgba(148,163,184,0.18)'; ctx.lineWidth = 0.7;
    ctx.beginPath(); ctx.moveTo(rackX, y); ctx.lineTo(rackX + rackW, y); ctx.stroke();
    if (i % 2 === 0) {
      ctx.fillStyle = 'rgba(226,232,240,0.45)'; ctx.font = '8px monospace'; ctx.textAlign = 'right';
      ctx.fillText(String(42 - i), rackX - 15, y + uH * 0.78);
    }
  }
  const gear = [
    { u: 41, h: 2, label: 'ODF', c: '#818CF8' }, { u: 39, h: 1, label: 'PP-01', c: '#38BDF8' }, { u: 38, h: 1, label: 'CM', c: '#64748B' },
    { u: 37, h: 1, label: 'SW-01', c: '#2DD4BF' }, { u: 36, h: 1, label: 'PP-02', c: '#38BDF8' }, { u: 35, h: 1, label: 'CM', c: '#64748B' },
    { u: 34, h: 1, label: 'SW-02', c: '#2DD4BF' }, { u: 31, h: 2, label: 'UPS', c: '#F59E0B' }, { u: 28, h: 2, label: 'APP-SRV', c: '#34D399' },
  ];
  const showGear = Math.floor(phase * gear.length * 1.8);
  gear.slice(0, showGear).forEach((g) => {
    const y = rackY + (42 - g.u) * uH;
    const h = g.h * uH;
    ctx.fillStyle = `${g.c}2e`; ctx.strokeStyle = g.c; ctx.lineWidth = 1.2;
    ctx.beginPath(); ctx.roundRect(rackX + 10, y + 2, rackW - 20, Math.max(5, h - 4), 3); ctx.fill(); ctx.stroke();
    ctx.fillStyle = '#eef7ff'; ctx.font = 'bold 10px monospace'; ctx.textAlign = 'left'; ctx.fillText(g.label, rackX + 18, y + h * 0.68);
    if (g.label.startsWith('PP') || g.label.startsWith('SW')) {
      for (let i = 0; i < 12; i++) { ctx.fillStyle = i % 2 ? '#4ade80' : '#7dd3fc'; ctx.fillRect(rackX + rackW - 92 + i * 6, y + h * 0.42, 3, 3); }
    }
  });
  ctx.strokeStyle = 'rgba(56,189,248,0.72)'; ctx.lineWidth = 2; ctx.setLineDash([7, 6]);
  ctx.beginPath(); ctx.moveTo(rackX + rackW - 32, rackY + 6 * uH); ctx.bezierCurveTo(W * 0.70, H * 0.22, W * 0.70, H * 0.42, rackX + rackW - 32, rackY + 11 * uH); ctx.stroke();
  ctx.strokeStyle = 'rgba(45,212,191,0.72)'; ctx.beginPath(); ctx.moveTo(rackX + 32, rackY + 20 * uH); ctx.bezierCurveTo(W * 0.18, H * 0.52, W * 0.20, H * 0.68, rackX + 52, rackY + 26 * uH); ctx.stroke(); ctx.setLineDash([]);
  ctx.fillStyle = color; ctx.font = 'bold 12px monospace'; ctx.textAlign = 'left'; ctx.fillText('MDF-01 · 42U FRONT ELEVATION', W * 0.62, H * 0.24);
  ['RU numbering', 'Patch panels', 'Switches', 'Cable managers', 'TIA-606 labels'].forEach((label, i) => {
    ctx.fillStyle = 'rgba(0,212,255,0.10)'; ctx.strokeStyle = 'rgba(0,212,255,0.22)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.roundRect(W * 0.62, H * 0.30 + i * 28, W * 0.22, 18, 5); ctx.fill(); ctx.stroke();
    ctx.fillStyle = 'rgba(240,246,255,0.75)'; ctx.font = '10px sans-serif'; ctx.fillText(label, W * 0.635, H * 0.30 + i * 28 + 12);
  });
}
function drawHospitalFloor(ctx: CanvasRenderingContext2D, W: number, H: number, phase: number, _c: string) {
  const rooms = [{x:0.08,y:0.22,w:0.18,h:0.22,l:'ICU'},{x:0.28,y:0.22,w:0.14,h:0.22,l:'OR'},{x:0.44,y:0.22,w:0.14,h:0.22,l:'MRI'},{x:0.60,y:0.22,w:0.14,h:0.22,l:'CT'},{x:0.08,y:0.52,w:0.10,h:0.18,l:'ED'},{x:0.20,y:0.52,w:0.10,h:0.18,l:'Pharm'},{x:0.32,y:0.52,w:0.24,h:0.18,l:'Nurse Sta.'},{x:0.58,y:0.52,w:0.16,h:0.18,l:'Lab'}];
  rooms.forEach((rm, i) => {
    const a = Math.min(1, Math.max(0, phase * rooms.length * 1.5 - i));
    if (!a) return;
    ctx.fillStyle = `rgba(74,222,128,${a*0.08})`; ctx.strokeStyle = `rgba(74,222,128,${a*0.7})`; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.rect(rm.x*W, rm.y*H, rm.w*W, rm.h*H); ctx.fill(); ctx.stroke();
    ctx.fillStyle = `rgba(74,222,128,${a*0.8})`; ctx.font = `bold ${Math.round(rm.w*W*0.12)}px monospace`; ctx.textAlign = 'center';
    ctx.fillText(rm.l, rm.x*W + rm.w*W/2, rm.y*H + rm.h*H/2+4);
  }); ctx.textAlign = 'left';
}
function drawFiberMap(ctx: CanvasRenderingContext2D, W: number, H: number, phase: number, _c: string) {
  const routes: [number,number][][] = [[[0.1,0.3],[0.4,0.3],[0.7,0.3],[0.9,0.3]],[[0.2,0.2],[0.2,0.5],[0.2,0.8]],[[0.5,0.2],[0.5,0.5],[0.5,0.7]],[[0.1,0.6],[0.4,0.6],[0.6,0.6],[0.9,0.6]]];
  routes.forEach((route, ri) => {
    const p = Math.min(1, Math.max(0, phase * routes.length * 1.5 - ri));
    ctx.strokeStyle = `rgba(251,191,36,${p*0.7})`; ctx.lineWidth = 2; ctx.beginPath();
    route.forEach(([x,y], i) => { if (!i) { ctx.moveTo(x*W,y*H); return; } const [px,py]=route[i-1] as [number,number]; ctx.lineTo(px*W+(x-px)*W*p, py*H+(y-py)*H*p); }); ctx.stroke();
    route.forEach(([x,y]) => { ctx.beginPath(); ctx.arc(x*W,y*H,5,0,Math.PI*2); ctx.fillStyle=`rgba(251,191,36,${p*0.8})`; ctx.fill(); });
  });
}
function drawSecurityOverlay(ctx: CanvasRenderingContext2D, W: number, H: number, phase: number, _c: string) {
  const cams = [{x:0.1,y:0.1,a:135},{x:0.9,y:0.1,a:225},{x:0.1,y:0.9,a:45},{x:0.9,y:0.9,a:315},{x:0.5,y:0.2,a:180},{x:0.5,y:0.8,a:0}];
  cams.forEach((cam, i) => {
    const ap = Math.min(1, Math.max(0, phase * cams.length * 1.5 - i));
    ctx.beginPath(); ctx.moveTo(cam.x*W,cam.y*H); ctx.arc(cam.x*W,cam.y*H,Math.min(W,H)*0.22*ap,(cam.a-40)*Math.PI/180,(cam.a+40)*Math.PI/180); ctx.closePath();
    ctx.fillStyle=`rgba(244,114,182,${ap*0.08})`; ctx.fill(); ctx.strokeStyle=`rgba(244,114,182,${ap*0.5})`; ctx.lineWidth=1; ctx.stroke();
    ctx.beginPath(); ctx.arc(cam.x*W,cam.y*H,5,0,Math.PI*2); ctx.fillStyle=`rgba(244,114,182,${ap})`; ctx.fill();
  });
}
function drawGenericAI(ctx: CanvasRenderingContext2D, W: number, H: number, phase: number, color: string) {
  const nodes: [number,number][] = [[0.15,0.5],[0.35,0.3],[0.35,0.7],[0.55,0.2],[0.55,0.5],[0.55,0.8],[0.75,0.35],[0.75,0.65],[0.9,0.5]];
  const edges: [number,number][] = [[0,1],[0,2],[1,3],[1,4],[2,4],[2,5],[3,6],[4,6],[4,7],[5,7],[6,8],[7,8]];
  edges.forEach(([a,b],i) => { const ap=Math.min(1,Math.max(0,phase*edges.length*1.5-i)); ctx.beginPath(); ctx.moveTo(nodes[a]![0]*W,nodes[a]![1]*H); ctx.lineTo(nodes[b]![0]*W,nodes[b]![1]*H); ctx.strokeStyle=`rgba(0,212,255,${ap*0.35})`; ctx.lineWidth=1.2; ctx.stroke(); });
  nodes.forEach(([x,y],i) => { const ap=Math.min(1,Math.max(0,phase*nodes.length*1.5-i)); ctx.beginPath(); ctx.arc(x*W,y*H,6,0,Math.PI*2); ctx.fillStyle=`rgba(0,212,255,${ap*0.9})`; ctx.fill(); });
  void color;
}

// ─── Construction Vision Engine ───────────────────────────────────────────────
function ConstructionVisionEngine({ phases, color }: { phases: string[]; color: string }): JSX.Element {
  const [active, setActive] = useState(0);
  const [running, setRunning] = useState(true);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (running) { timer.current = setInterval(() => setActive(p => p >= phases.length - 1 ? 0 : p + 1), 1200); }
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [running, phases.length]);
  return (
    <div style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(0,212,255,0.12)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,212,255,0.1)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Zap size={11} style={{ color }}/>
        <span style={{ fontSize: 9.5, fontWeight: 800, color, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em' }}>4D Construction Vision</span>
        <span style={{ fontSize: 8.5, color: 'rgba(240,246,255,0.3)', marginLeft: 4, fontFamily: 'monospace' }}>AI CONCEPTUAL</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
          <button onClick={() => setRunning(v => !v)} style={{ background: 'transparent', border: `1px solid ${color}40`, borderRadius: 5, padding: '2px 7px', cursor: 'pointer', color, fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
            {running ? <><Pause size={8}/> Auto Playing</> : <><Play size={8}/> Resume</>}
          </button>
          {active > 0 && !running && <button onClick={() => { setActive(0); setRunning(false); }} style={{ background: 'transparent', border: '1px solid rgba(240,246,255,0.12)', borderRadius: 5, padding: '2px 7px', cursor: 'pointer', color: 'rgba(240,246,255,0.4)', fontSize: 9 }}>Reset</button>}
        </div>
      </div>
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
        {phases.map((ph, i) => {
          const isDone = i < active, isNow = i === active;
          return (
            <button key={i} onClick={() => { setRunning(false); setActive(i); }}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 7px', borderRadius: 6, border: `1px solid ${isNow ? color : isDone ? `${color}30` : 'rgba(240,246,255,0.07)'}`, background: isNow ? `${color}10` : 'transparent', cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: isNow ? color : isDone ? `${color}60` : 'rgba(240,246,255,0.15)', flexShrink: 0 }}/>
              <span style={{ fontSize: 10, color: isNow ? C.text : isDone ? 'rgba(240,246,255,0.45)' : 'rgba(240,246,255,0.25)', fontWeight: isNow ? 700 : 400 }}>{ph}</span>
              {isDone && <CheckCircle2 size={9} style={{ color: `${color}80`, marginLeft: 'auto' }}/>}
            </button>
          );
        })}
      </div>
      <style>{`@keyframes wpr { from{width:0} to{width:100%} }`}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// R2 PREVIEW MODAL — Enhanced with full "What You Receive" intelligence
// ═══════════════════════════════════════════════════════════════════════════════
interface PreviewModalProps {
  serviceType: ServiceAnimationType;
  serviceName: string;
  price: number;
  delivery: number;
  onClose: () => void;
  onOrder: () => void;
}

type PreviewTab = 'deliverables' | 'timeline' | 'upgrades' | 'why-r2';

export function R2PreviewModal({ serviceType, serviceName, price: _price, delivery, onClose, onOrder }: PreviewModalProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null!);
  const cfg = SERVICE_ANIMATION_CONFIG[serviceType];
  const det = SERVICE_DELIVERY_DETAILS[serviceType];
  const [playing, setPlaying] = useState(true);
  const [currentStage, setCurrentStage] = useState(0);
  const [activeTab, setActiveTab] = useState<PreviewTab>('deliverables');

  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => setCurrentStage(s => Math.min(s + 1, cfg.stages.length - 1)), 1600);
    return () => clearInterval(t);
  }, [playing, cfg.stages.length]);

  useCanvasAnimation(canvasRef, serviceType, playing);

  const tabs: [PreviewTab, string][] = [['deliverables', 'What You Receive'], ['timeline', 'Timeline'], ['upgrades', 'Optional Upgrades'], ['why-r2', 'Why R2']];

  return (
    <div data-testid="preview-modal"
      style={{ position: 'fixed', inset: 0, background: 'rgba(1,8,18,0.96)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

      <div style={{ width: '100%', maxWidth: 960, background: '#010e22', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 18, overflow: 'hidden', boxShadow: '0 0 80px rgba(0,212,255,0.06)' }}>

        {/* ── Top Bar ─────────────────────────────────────────────────────── */}
        <div style={{ padding: '11px 16px', borderBottom: '1px solid rgba(0,212,255,0.1)', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(0,0,0,0.6)' }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: `${cfg.color}15`, border: `1px solid ${cfg.color}40`, display: 'grid', placeItems: 'center' }}>
            <Zap size={13} style={{ color: cfg.color }}/>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{serviceName}</div>
            <div style={{ fontSize: 9, fontFamily: 'monospace', color: 'rgba(240,246,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>R2 Service Preview · Cinematic Mode</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="r2pm-topbar-extras" style={{ display: 'flex', gap: 6 }}>
              {det.standards.slice(0, 3).map(s => (
                <span key={s} style={{ fontFamily: 'monospace', fontSize: 8.5, padding: '2px 6px', background: `${cfg.color}10`, border: `1px solid ${cfg.color}25`, borderRadius: 4, color: `${cfg.color}80` }}>{s}</span>
              ))}
            </div>
            <span className="r2pm-topbar-extras" style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(240,246,255,0.35)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Clock size={10}/>est. {delivery}h turnaround
            </span>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(240,246,255,0.35)', display: 'flex' }}><X size={16}/></button>
          </div>
        </div>

        {/* ── Canvas Animation ─────────────────────────────────────────────── */}
        <div style={{ position: 'relative', background: '#010812', aspectRatio: '16/6' }}>
          <canvas ref={canvasRef} width={960} height={360} style={{ width: '100%', height: '100%', display: 'block' }}/>
          <div style={{ position: 'absolute', top: 12, left: 14, fontFamily: 'monospace', fontSize: 10, fontWeight: 800, color: 'rgba(0,212,255,0.5)', letterSpacing: '0.15em' }}>R2 ENGINE</div>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(1,8,18,0.9))', padding: '30px 18px 12px', pointerEvents: 'none' }}>
            <div style={{ fontSize: 12, color: 'rgba(240,246,255,0.65)', fontStyle: 'italic' }}>{cfg.tagline}</div>
          </div>
          <button onClick={() => setPlaying(v => !v)}
            style={{ position: 'absolute', top: 12, right: 14, background: 'rgba(0,0,0,0.6)', border: `1px solid ${cfg.color}40`, borderRadius: 7, padding: '5px 9px', cursor: 'pointer', color: cfg.color, display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700 }}>
            {playing ? <><Pause size={10}/> Pause</> : <><Play size={10}/> Play</>}
          </button>
        </div>

        {/* ── AI Stage Progress ────────────────────────────────────────────── */}
        <div style={{ padding: '10px 16px', background: 'rgba(0,0,0,0.4)', borderBottom: '1px solid rgba(0,212,255,0.07)', display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {cfg.stages.map((stage, i) => {
            const done = i <= currentStage, active = i === currentStage;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: done ? C.text : 'rgba(240,246,255,0.22)' }}>
                <div style={{ width: 14, height: 14, borderRadius: '50%', background: done ? `${cfg.color}20` : 'rgba(240,246,255,0.04)', border: `1px solid ${done ? cfg.color : 'rgba(240,246,255,0.1)'}`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  {done && <CheckCircle2 size={8} style={{ color: cfg.color }}/>}
                </div>
                {active ? <strong>{stage}</strong> : stage}
                {i < cfg.stages.length - 1 && <ChevronRight size={9} style={{ color: 'rgba(240,246,255,0.15)', flexShrink: 0 }}/>}
              </div>
            );
          })}
        </div>

        {/* ── Tab Navigation ───────────────────────────────────────────────── */}
        <div className="r2pm-tabs" style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(0,212,255,0.1)', background: 'rgba(0,0,0,0.3)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any }}>
          {tabs.map(([id, label]) => (
            <button key={id} onClick={() => setActiveTab(id)}
              style={{ padding: '9px 18px', background: 'transparent', border: 'none', borderBottom: `2px solid ${activeTab === id ? cfg.color : 'transparent'}`, cursor: 'pointer', fontSize: 11.5, fontWeight: activeTab === id ? 700 : 400, color: activeTab === id ? cfg.color : 'rgba(240,246,255,0.45)', transition: 'all 0.15s', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Tab Content ──────────────────────────────────────────────────── */}
        <div style={{ padding: '16px 20px', minHeight: 240 }}>

          {/* DELIVERABLES TAB */}
          {activeTab === 'deliverables' && (
            <div className="r2pm-2col r2pm-tab-enter" style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 20 }}>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: 9.5, color: 'rgba(0,212,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Included Deliverables</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {det.detailed_delivers.map((d, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 12px', background: 'rgba(240,246,255,0.02)', border: '1px solid rgba(240,246,255,0.07)', borderRadius: 8 }}>
                      <CheckCircle2 size={14} style={{ color: cfg.color, flexShrink: 0, marginTop: 2 }}/>
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, marginBottom: 2 }}>{d.name}</div>
                        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{d.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: 8 }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(0,212,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 5 }}>Applicable Standards</div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {det.standards.map(s => <span key={s} style={{ fontFamily: 'monospace', fontSize: 9.5, padding: '2px 7px', background: `${cfg.color}10`, border: `1px solid ${cfg.color}25`, borderRadius: 4, color: cfg.color }}>{s}</span>)}
                  </div>
                </div>
              </div>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: 9.5, color: 'rgba(0,212,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Sample Outputs</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                  {det.sample_outputs.map((o, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <FileText size={12} style={{ color: C.amber, flexShrink: 0, marginTop: 2 }}/>
                      <span style={{ fontSize: 11.5, color: C.muted }}>{o}</span>
                    </div>
                  ))}
                </div>
                <ConstructionVisionEngine phases={cfg.constructionPhases} color={cfg.color}/>
              </div>
            </div>
          )}

          {/* TIMELINE TAB */}
          {activeTab === 'timeline' && (
            <div className="r2pm-tab-enter">
              <div style={{ fontFamily: 'monospace', fontSize: 9.5, color: 'rgba(0,212,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>Typical Turnaround — {delivery}h from brief confirmation</div>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 18, top: 20, bottom: 20, width: 1, background: 'rgba(0,212,255,0.15)' }}/>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {det.timeline.map((ph, i) => (
                    <div key={i} style={{ display: 'flex', gap: 20, paddingLeft: 42, position: 'relative' }}>
                      <div style={{ position: 'absolute', left: 12, top: 10, width: 12, height: 12, borderRadius: '50%', background: '#010e22', border: `2px solid ${cfg.color}`, zIndex: 1 }}/>
                      <div style={{ flex: 1, padding: '10px 14px', background: 'rgba(240,246,255,0.02)', border: '1px solid rgba(240,246,255,0.08)', borderRadius: 9 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                          <span style={{ fontSize: 12.5, fontWeight: 800, color: C.text }}>{ph.phase}</span>
                          <span style={{ fontFamily: 'monospace', fontSize: 9.5, padding: '2px 7px', background: `${cfg.color}12`, border: `1px solid ${cfg.color}30`, borderRadius: 20, color: cfg.color }}>{ph.duration}</span>
                        </div>
                        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>{ph.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(74,222,128,0.05)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 9, display: 'flex', gap: 10, alignItems: 'center' }}>
                <AlertCircle size={14} style={{ color: C.green, flexShrink: 0 }}/>
                <div style={{ fontSize: 11, color: C.muted }}>
                  Timelines are indicative and begin from brief confirmation. Express turnaround upgrade available — see Optional Upgrades tab.
                  All deliverables are reviewed by a <strong style={{ color: C.text }}>registered professional engineer</strong> before issue.
                </div>
              </div>
            </div>
          )}

          {/* UPGRADES TAB */}
          {activeTab === 'upgrades' && (
            <div className="r2pm-tab-enter">
              <div style={{ fontFamily: 'monospace', fontSize: 9.5, color: 'rgba(0,212,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>Optional Add-Ons & Upgrades</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10, marginBottom: 16 }}>
                {det.upgrades.map((u, i) => (
                  <div key={i} style={{ padding: '12px 14px', background: 'rgba(240,246,255,0.02)', border: '1px solid rgba(240,246,255,0.1)', borderRadius: 10, position: 'relative' }}>
                    {u.tag && (
                      <span style={{ position: 'absolute', top: 8, right: 8, fontFamily: 'monospace', fontSize: 7.5, fontWeight: 800, padding: '2px 6px', background: u.tag === 'POPULAR' ? 'rgba(255,107,53,0.15)' : u.tag === 'PREMIUM' ? 'rgba(251,191,36,0.12)' : 'rgba(0,212,255,0.1)', border: `1px solid ${u.tag === 'POPULAR' ? 'rgba(255,107,53,0.4)' : u.tag === 'PREMIUM' ? 'rgba(251,191,36,0.3)' : 'rgba(0,212,255,0.3)'}`, color: u.tag === 'POPULAR' ? C.orange : u.tag === 'PREMIUM' ? C.amber : C.cyan, borderRadius: 20, textTransform: 'uppercase' }}>{u.tag}</span>
                    )}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <Plus size={13} style={{ color: cfg.color, flexShrink: 0, marginTop: 2 }}/>
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, marginBottom: 3 }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{u.description}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ padding: '10px 14px', background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: 9, fontSize: 11, color: C.muted }}>
                <Package size={12} style={{ display: 'inline', marginRight: 6, color: C.cyan }}/> Upgrades are explained during the free package review — click <strong style={{ color: C.text }}>View Package Options</strong> when you are ready to see the full scope, not a payment prompt.
              </div>
            </div>
          )}

          {/* WHY R2 TAB */}
          {activeTab === 'why-r2' && (
            <div className="r2pm-2col r2pm-tab-enter" style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 20 }}>
              <div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 16 }}>
                  <Sparkles size={18} style={{ color: cfg.color, flexShrink: 0, marginTop: 2 }}/>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text, lineHeight: 1.4 }}>Why R2 Recommends This Package</div>
                </div>
                <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.75, marginBottom: 18 }}>{det.why_r2}</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[['AI-Powered', 'Claude Sonnet for intelligent analysis'], ['PE-Reviewed', 'Every deliverable signed by a registered engineer'], ['Standards-Compliant', `${det.standards.slice(0, 2).join(' · ')} and more`], ['Construction-Ready', 'DWG + PDF in your project standard format']].map(([t, d]) => (
                    <div key={t} style={{ padding: '9px 11px', background: `${cfg.color}06`, border: `1px solid ${cfg.color}18`, borderRadius: 8 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: cfg.color, marginBottom: 3 }}>{t}</div>
                      <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.4 }}>{d}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 9.5, color: 'rgba(0,212,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>Scope Includes</div>
                {det.detailed_delivers.map(d => (
                  <div key={d.name} style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
                    <Star size={10} style={{ color: cfg.color, flexShrink: 0 }}/><span style={{ fontSize: 11, color: C.muted }}>{d.name}</span>
                  </div>
                ))}
                <div style={{ marginTop: 8, padding: '9px 11px', background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 8 }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 8.5, color: C.green, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 5 }}>Delivery Format</div>
                  <div style={{ fontSize: 10.5, color: C.muted }}>PDF · DWG (AutoCAD 2018+) · Secure delivery portal · Revision history</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── CTA ─────────────────────────────────────────────────────────── */}
        <div style={{ padding: '12px 20px 18px', display: 'flex', gap: 10, borderTop: '1px solid rgba(0,212,255,0.08)' }}>
          <button data-testid="preview-order-btn" onClick={onOrder}
            style={{ flex: 1, background: C.orange, color: '#010812', border: 'none', borderRadius: 10, padding: '12px 0', fontWeight: 800, fontSize: 13.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            View Package Options <ChevronRight size={15}/>
          </button>
          <button onClick={onClose}
            style={{ padding: '12px 22px', background: 'transparent', border: '1px solid rgba(240,246,255,0.12)', borderRadius: 10, cursor: 'pointer', color: 'rgba(240,246,255,0.4)', fontSize: 12 }}>
            Close
          </button>
        </div>
        <style>{`
          @keyframes r2pm-fade-in { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
          .r2pm-tab-enter { animation: r2pm-fade-in 0.2s ease both; }
          .r2pm-tabs { scrollbar-width: none; }
          .r2pm-tabs::-webkit-scrollbar { display: none; }
          @media (max-width: 640px) {
            .r2pm-2col { grid-template-columns: 1fr !important; }
            .r2pm-tabs button { padding: 9px 12px !important; font-size: 10.5px !important; }
            .r2pm-topbar-extras { display: none !important; }
          }
        `}</style>
      </div>
    </div>
  );
}
